import { Link } from 'react-router-dom';

import PropertyVisual from '../components/PropertyVisual';
import ResidenceGrid from '../components/ResidenceGrid';
import { useLanguage } from '../context/LanguageContext';

const content = {
  en: {
    heroEyebrow: 'Sveti Stefan · Montenegro',
    heroTitle: <>A private rhythm<br />on the Adriatic</>,
    heroText: 'Private residences in one of Montenegro’s most iconic locations.',
    introEyebrow: 'Adriatic Masterpiece Residence',
    introTitle: 'Contemporary apartments above the coast',
    introText: 'AMR brings together generous interiors, private parking and direct contact with the residence team in the heart of Sveti Stefan.',
    residencesTitle: 'Choose your apartment',
    residencesText: 'From the island-view penthouse to calm garden and sea-view apartments, each space has its own layout and atmosphere.',
    processTitle: 'See what is available for your stay',
    process: [
      ['01', 'Dates', 'Choose your arrival and departure dates.'],
      ['02', 'Guests', 'Add the number of adults and children travelling.'],
      ['03', 'Availability', 'See the apartments available for your stay.'],
    ],
    locationEyebrow: 'The setting',
    locationTitle: 'Sveti Stefan, an icon of Montenegro and the Adriatic',
    locationText: 'A fortified island village, crystal-clear water and the scenery of the Budva Riviera set an unhurried rhythm for each day.',
    closing: 'Your apartment on the Montenegrin coast',
  },
  ru: {
    heroEyebrow: 'Святой‑Стефан · Черногория',
    heroTitle: <>Свой ритм жизни<br />на Адриатике</>,
    heroText: 'Апартаменты для короткого и длительного отдыха рядом с главным символом Черногории на Адриатике.',
    introEyebrow: 'Adriatic Masterpiece Residence',
    introTitle: 'Современные апартаменты над побережьем',
    introText: 'AMR объединяет просторные интерьеры, частную парковку и прямое общение с командой объекта в самом сердце Святого‑Стефана.',
    residencesTitle: 'Выберите апартамент',
    residencesText: 'От пентхауса с видом на остров до тихих апартаментов с зелёной или морской панорамой — у каждого пространства свой характер.',
    processTitle: 'Посмотрите, что свободно на ваши даты',
    process: [
      ['01', 'Даты', 'Выберите дату заезда и дату выезда.'],
      ['02', 'Гости', 'Укажите количество взрослых и детей.'],
      ['03', 'Доступность', 'Посмотрите свободные апартаменты на выбранный период.'],
    ],
    locationEyebrow: 'Место',
    locationTitle: 'Святой‑Стефан — символ Черногории и Адриатики',
    locationText: 'Остров‑крепость, прозрачные пляжи и пейзажи Будванской ривьеры задают ритм каждого дня.',
    closing: 'Ваш апартамент на побережье Черногории',
  },
};

function HomePage() {
  const { language, t } = useLanguage();
  const copy = content[language];

  return (
    <>
      <section className="hero">
        <PropertyVisual src="/images/property/190.jpg" alt="Adriatic Masterpiece Residence with Sveti Stefan island" eager className="hero__visual">
          <div className="hero__overlay" />
        </PropertyVisual>
        <div className="hero__content">
          <span className="eyebrow eyebrow_light">{copy.heroEyebrow}</span>
          <h1>{copy.heroTitle}</h1>
          <p>{copy.heroText}</p>
          <div className="hero__actions">
            <Link className="button button_light" to="/book">{t('common.book')}</Link>
            <Link className="text-link text-link_light" to="/residences">{t('common.discover')} <span>↗</span></Link>
          </div>
        </div>
        <div className="hero__folio">42°15′15.3′′ N<br />18°53′50.6′′ E</div>
      </section>

      <section className="section intro-section">
        <div className="section-heading section-heading_center">
          <span className="eyebrow">{copy.introEyebrow}</span>
          <h2>{copy.introTitle}</h2>
          <p>{copy.introText}</p>
        </div>
        <div className="facts-strip">
          <div><strong>200 m</strong><span>{language === 'ru' ? 'До пляжа' : 'To the beach'}</span></div>
          <div><strong>Parking</strong><span>{language === 'ru' ? 'Частная парковка' : 'At the residence'}</span></div>
          <div><strong>Transfer</strong><span>{language === 'ru' ? 'По запросу' : 'Available on request'}</span></div>
        </div>
      </section>

      <section className="section section_wide residences-preview">
        <div className="section-heading section-heading_split">
          <h2>{copy.residencesTitle}</h2>
          <p>{copy.residencesText}</p>
        </div>
        <ResidenceGrid />
      </section>

      <section className="booking-process">
        <div className="booking-process__heading">
          <span className="eyebrow eyebrow_light">{t('common.directBooking')}</span>
          <h2>{copy.processTitle}</h2>
        </div>
        <div className="booking-process__steps">
          {copy.process.map(([number, title, text]) => (
            <div className="booking-process__step" key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{text}</p>
            </div>
          ))}
        </div>
        <Link className="button button_light" to="/book">{t('common.book')}</Link>
      </section>

      <section className="section section_wide location-preview">
        <PropertyVisual src="/images/property/120.jpg" alt="Sveti Stefan, Montenegro" className="location-preview__visual" />
        <div className="location-preview__content">
          <span className="eyebrow">{copy.locationEyebrow}</span>
          <h2>{copy.locationTitle}</h2>
          <p>{copy.locationText}</p>
          <Link className="text-link" to="/location">{t('navigation.location')} <span>↗</span></Link>
        </div>
      </section>

      <section className="closing-cta">
        <span className="eyebrow">AMR · Sveti Stefan</span>
        <h2>{copy.closing}</h2>
        <Link className="button" to="/book">{t('common.book')}</Link>
      </section>
    </>
  );
}

export default HomePage;
